/**
 * O ajudante do jogo: um chat de suporte com IA, flutuando no canto.
 *
 * Fica escondido até o servidor dizer que está ligado — configurado, com chave
 * e com modelo escolhido. Um botão de ajuda que abre e responde "não
 * configurado" é pior do que botão nenhum.
 *
 * O histórico da conversa vive no SERVIDOR. Esta tela manda a pergunta e o
 * identificador da conversa; quem remonta o contexto é o backend. Deixar o
 * cliente mandar o histórico seria deixá-lo escrever falas na boca do
 * assistente — inclusive a instrução de sistema.
 */

import { useEffect, useRef, useState } from 'react';
import { api, messageOf } from '../api/client.js';

interface Line {
  readonly role: 'user' | 'assistant';
  readonly body: string;
}

/** Onde a conversa em curso é lembrada entre recarregamentos da página. */
const STORAGE_KEY = 'oz:ajuda:conversa';

export function HelpChat(): JSX.Element | null {
  const [available, setAvailable] = useState(false);
  const [displayName, setDisplayName] = useState('Ajudante de Oz');
  const [greeting, setGreeting] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [lines, setLines] = useState<Line[]>([]);
  const [draft, setDraft] = useState('');
  const [thinking, setThinking] = useState(false);
  const [error, setError] = useState('');
  const conversationRef = useRef<number>(0);
  const logRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    api
      .get<{ available: boolean; displayName: string; greeting: string | null }>('/api/ajuda/status')
      .then((status) => {
        setAvailable(status.available);
        setDisplayName(status.displayName);
        setGreeting(status.greeting);
      })
      .catch(() => setAvailable(false));

    try {
      const saved = Number(window.localStorage.getItem(STORAGE_KEY) ?? 0);
      if (saved > 0) conversationRef.current = saved;
    } catch {
      // Armazenamento bloqueado: a conversa começa do zero a cada visita, o
      // que é uma perda pequena e não vale um erro na tela.
    }
  }, []);

  /** Reabre a conversa de onde parou. */
  useEffect(() => {
    if (!open || conversationRef.current === 0 || lines.length > 0) return;

    void api
      .get<{ messages: Array<{ role: string; body: string }> }>(
        `/api/ajuda/conversa/${conversationRef.current}`,
      )
      .then((data) => {
        setLines(data.messages
          .filter((m): m is Line => m.role === 'user' || m.role === 'assistant')
          .map((m) => ({ role: m.role, body: m.body })));
      })
      .catch(() => {
        // Conversa apagada ou de outra sessão: começa uma nova.
        conversationRef.current = 0;
      });
  }, [open, lines.length]);

  useEffect(() => {
    const box = logRef.current;
    if (box) box.scrollTop = box.scrollHeight;
  }, [lines, thinking]);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  if (!available) return null;

  const send = async () => {
    const question = draft.trim();
    if (question === '' || thinking) return;

    setDraft('');
    setError('');
    setLines((old) => [...old, { role: 'user', body: question }]);
    setThinking(true);

    try {
      const answer = await api.post<{ conversationId: number; answer: string }>(
        '/api/ajuda/mensagem',
        { message: question, conversationId: conversationRef.current || undefined },
      );

      conversationRef.current = answer.conversationId;
      try {
        window.localStorage.setItem(STORAGE_KEY, String(answer.conversationId));
      } catch {
        // Idem: não poder lembrar não impede de responder agora.
      }

      setLines((old) => [...old, { role: 'assistant', body: answer.answer }]);
    } catch (e) {
      setError(messageOf(e, 'O assistente não respondeu.'));
    } finally {
      setThinking(false);
    }
  };

  return (
    <>
      <button
        type="button"
        className={`help-fab ${open ? 'open' : ''}`}
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-label={open ? 'Fechar a ajuda' : 'Abrir a ajuda'}
        title={displayName}
      >
        {open ? '✕' : '💬'}
      </button>

      {open && (
        <div className="help-panel" role="dialog" aria-label={displayName}>
          <header className="help-panel-head">
            <span className="help-dot" aria-hidden="true" />
            <strong>{displayName}</strong>
            <button
              type="button"
              className="help-clear"
              title="Começar uma conversa nova"
              onClick={() => {
                conversationRef.current = 0;
                setLines([]);
                setError('');
                try {
                  window.localStorage.removeItem(STORAGE_KEY);
                } catch { /* sem armazenamento, nada a limpar */ }
              }}
            >
              ↺
            </button>
          </header>

          <div className="help-log" ref={logRef}>
            {lines.length === 0 && (
              <p className="help-greeting">
                {greeting ?? `Oi! Sou o ${displayName}. Pergunte sobre as regras, o editor de mapas, a campanha ou a loja.`}
              </p>
            )}

            {lines.map((line, index) => (
              <div key={index} className={`help-line ${line.role}`}>
                {line.body}
              </div>
            ))}

            {thinking && <div className="help-line assistant thinking"><span /><span /><span /></div>}
            {error && <p className="help-error">⚠️ {error}</p>}
          </div>

          <form
            className="help-form"
            onSubmit={(event) => {
              event.preventDefault();
              void send();
            }}
          >
            <textarea
              ref={inputRef}
              value={draft}
              rows={2}
              maxLength={4000}
              placeholder="Como posso ajudar?"
              onChange={(event) => setDraft(event.target.value)}
              onKeyDown={(event) => {
                // Enter manda, Shift+Enter quebra linha — o costume de qualquer
                // chat. Sem isso, quem escreve uma pergunta longa manda pela
                // metade sem querer.
                if (event.key === 'Enter' && !event.shiftKey) {
                  event.preventDefault();
                  void send();
                }
              }}
            />
            <button type="submit" disabled={thinking || draft.trim() === ''}>
              {thinking ? '…' : 'Enviar'}
            </button>
          </form>
        </div>
      )}
    </>
  );
}
